/**
 * 3x3 matrix and 3-vector helpers.
 *
 * # Storage convention: COLUMN-MAJOR
 *
 * Every `Mat3` in this project stores element (row `r`, column `c`) at index
 *
 *     m[c * 3 + r]
 *
 * The three columns are therefore contiguous: `[c0r0 c0r1 c0r2 | c1r0 ... ]`.
 * This matches GLSL's `mat3`, and it is what `gl.uniformMatrix3fv(loc, false, m)`
 * expects with `transpose = false`. Never pass `transpose = true`; never store
 * row-major "just here".
 *
 * Consequences to keep in mind:
 *  - `mulVec(M, v)[r] === sum_c M[c*3+r] * v[c]` — a linear combination of columns.
 *  - `mul(A, B)` treats `B` as a list of three column vectors: column j of the
 *    result is `A * column_j(B)`.
 *  - `transpose` is a real 6-element shuffle, not an index-order trick.
 *
 * This convention is the single most likely source of a silent bug in the
 * project, so it is stated once here and never restated differently.
 *
 * @module core/mat3
 */

import type { Mat3, Vec3 } from './types.ts';

/** Index of element (row, column) in a column-major 3x3 matrix. */
export function idx(row: number, col: number): number {
  return col * 3 + row;
}

/** Read element (row, col). */
export function get(m: Mat3, row: number, col: number): number {
  return m[col * 3 + row]!;
}

/** Write element (row, col). Returns `m`. */
export function set(m: Mat3, row: number, col: number, value: number): Mat3 {
  m[col * 3 + row] = value;
  return m;
}

/** The 3x3 identity. */
export function identity(): Mat3 {
  const m = new Float64Array(9);
  m[0] = 1;
  m[4] = 1;
  m[8] = 1;
  return m;
}

/** A diagonal matrix from three values. */
export function diag(a: number, b: number, c: number): Mat3 {
  const m = new Float64Array(9);
  m[0] = a;
  m[4] = b;
  m[8] = c;
  return m;
}

/** Copy a matrix. */
export function clone(m: Mat3): Mat3 {
  return Float64Array.from(m);
}

/** Build from three column vectors, given row-major as `[c0r0,c0r1,c0r2, ...]`. */
export function fromColumns(values: readonly number[]): Mat3 {
  if (values.length !== 9) throw new Error(`fromColumns: expected 9 values, got ${values.length}`);
  return Float64Array.from(values);
}

/** Build from nine values given in row-major reading order. */
export function fromRows(values: readonly number[]): Mat3 {
  if (values.length !== 9) throw new Error(`fromRows: expected 9 values, got ${values.length}`);
  const m = new Float64Array(9);
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) m[c * 3 + r] = values[r * 3 + c]!;
  }
  return m;
}

/** Build a matrix from a list of nine values plus an explicit `'rows'|'cols'` order. */
export function fromList(values: readonly number[], order: 'rows' | 'cols' = 'rows'): Mat3 {
  return order === 'rows' ? fromRows(values) : fromColumns(values);
}

/** Read the matrix back out in row-major reading order (for tests and JSON). */
export function toRows(m: Mat3): number[] {
  const out: number[] = [];
  for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) out.push(m[c * 3 + r]!);
  return out;
}

/** Transpose. */
export function transpose(m: Mat3): Mat3 {
  const o = new Float64Array(9);
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) o[c * 3 + r] = m[r * 3 + c]!;
  }
  return o;
}

/**
 * Matrix product `A * B`.
 *
 * Column `j` of the result is `A` applied to column `j` of `B`.
 */
export function mul(A: Mat3, B: Mat3): Mat3 {
  const o = new Float64Array(9);
  for (let j = 0; j < 3; j++) {
    const b0 = B[j * 3 + 0]!;
    const b1 = B[j * 3 + 1]!;
    const b2 = B[j * 3 + 2]!;
    o[j * 3 + 0] = A[0]! * b0 + A[3]! * b1 + A[6]! * b2;
    o[j * 3 + 1] = A[1]! * b0 + A[4]! * b1 + A[7]! * b2;
    o[j * 3 + 2] = A[2]! * b0 + A[5]! * b1 + A[8]! * b2;
  }
  return o;
}

/** Product of three matrices, `A * B * C`. */
export function mul3(A: Mat3, B: Mat3, C: Mat3): Mat3 {
  return mul(mul(A, B), C);
}

/** `M * v`. */
export function mulVec(m: Mat3, v: ArrayLike<number>, out?: Vec3): Vec3 {
  const o = out ?? new Float64Array(3);
  const x = v[0]!;
  const y = v[1]!;
  const z = v[2]!;
  o[0] = m[0]! * x + m[3]! * y + m[6]! * z;
  o[1] = m[1]! * x + m[4]! * y + m[7]! * z;
  o[2] = m[2]! * x + m[5]! * y + m[8]! * z;
  return o;
}

/** `M * v + b`. */
export function mulVecAdd(m: Mat3, v: ArrayLike<number>, b: ArrayLike<number>, out?: Vec3): Vec3 {
  const o = mulVec(m, v, out);
  o[0] = o[0]! + b[0]!;
  o[1] = o[1]! + b[1]!;
  o[2] = o[2]! + b[2]!;
  return o;
}

/** `Mᵀ * v` without materialising the transpose. */
export function mulTransposeVec(m: Mat3, v: ArrayLike<number>, out?: Vec3): Vec3 {
  const o = out ?? new Float64Array(3);
  const x = v[0]!;
  const y = v[1]!;
  const z = v[2]!;
  o[0] = m[0]! * x + m[1]! * y + m[2]! * z;
  o[1] = m[3]! * x + m[4]! * y + m[5]! * z;
  o[2] = m[6]! * x + m[7]! * y + m[8]! * z;
  return o;
}

/** Element-wise sum. */
export function add(A: Mat3, B: Mat3): Mat3 {
  const o = new Float64Array(9);
  for (let i = 0; i < 9; i++) o[i] = A[i]! + B[i]!;
  return o;
}

/** Element-wise difference. */
export function sub(A: Mat3, B: Mat3): Mat3 {
  const o = new Float64Array(9);
  for (let i = 0; i < 9; i++) o[i] = A[i]! - B[i]!;
  return o;
}

/** Scalar multiple. */
export function scale(m: Mat3, k: number): Mat3 {
  const o = new Float64Array(9);
  for (let i = 0; i < 9; i++) o[i] = m[i]! * k;
  return o;
}

/** Determinant, via the column vectors. */
export function det(m: Mat3): number {
  const a = m[0]!;
  const b = m[1]!;
  const c = m[2]!;
  const d = m[3]!;
  const e = m[4]!;
  const f = m[5]!;
  const g = m[6]!;
  const h = m[7]!;
  const i = m[8]!;
  return a * (e * i - f * h) - d * (b * i - c * h) + g * (b * f - c * e);
}

/** Trace. */
export function trace(m: Mat3): number {
  return m[0]! + m[4]! + m[8]!;
}

/** Frobenius norm. */
export function frobenius(m: Mat3): number {
  let s = 0;
  for (let i = 0; i < 9; i++) s += m[i]! * m[i]!;
  return Math.sqrt(s);
}

/** Largest absolute element-wise difference, `max |A - B|`. */
export function maxAbsDiff(A: Mat3, B: Mat3): number {
  let d = 0;
  for (let i = 0; i < 9; i++) d = Math.max(d, Math.abs(A[i]! - B[i]!));
  return d;
}

/** True when `max |A - B| <= tol`. */
export function equals(A: Mat3, B: Mat3, tol = 0): boolean {
  return maxAbsDiff(A, B) <= tol;
}

/** True when the matrix is symmetric to within `tol`. */
export function isSymmetric(m: Mat3, tol = 1e-12): boolean {
  return (
    Math.abs(m[1]! - m[3]!) <= tol &&
    Math.abs(m[2]! - m[6]!) <= tol &&
    Math.abs(m[5]! - m[7]!) <= tol
  );
}

/** True when `MᵀM = I` to within `tol`. */
export function isOrthonormal(m: Mat3, tol = 1e-12): boolean {
  return equals(mul(transpose(m), m), identity(), tol);
}

/** Apply a function to each element, returning a new matrix. */
export function map(m: Mat3, f: (value: number, row: number, col: number) => number): Mat3 {
  const o = new Float64Array(9);
  for (let c = 0; c < 3; c++) {
    for (let r = 0; r < 3; r++) o[c * 3 + r] = f(m[c * 3 + r]!, r, c);
  }
  return o;
}

/** Human-readable dump, for test failure messages. */
export function format(m: Mat3, digits = 6): string {
  const rows = toRows(m).map((v) => v.toFixed(digits).padStart(digits + 5));
  return `[${rows.slice(0, 3).join(' ')}]\n[${rows.slice(3, 6).join(' ')}]\n[${rows.slice(6, 9).join(' ')}]`;
}

/** A vector of three zeros. */
export function zeroVec(): Vec3 {
  return new Float64Array(3);
}

/** Build a Vec3 from three numbers. */
export function vec3(x: number, y: number, z: number): Vec3 {
  return Float64Array.of(x, y, z);
}

/** Largest absolute element-wise difference between two vectors. */
export function vecMaxAbsDiff(a: ArrayLike<number>, b: ArrayLike<number>): number {
  let d = 0;
  for (let i = 0; i < 3; i++) d = Math.max(d, Math.abs(a[i]! - b[i]!));
  return d;
}
